(function (root, factory) {
  const create = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = create;
  root.FabrikoneStudioStorage = create;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';
  const TYPES = ['projects', 'experiments'];
  const FIELDS = ['date', 'wax', 'waxAmount', 'fragrance', 'rate', 'dye', 'dyeAmount', 'wick', 'container', 'temperature', 'observations', 'category', 'quantity', 'unit', 'source'];
  const clone = value => JSON.parse(JSON.stringify(value));
  const fail = (code, message) => { const error = new Error(message || code); error.code = code; throw error; };
  const canonical = value => JSON.stringify(sort(value));
  function sort(value) {
    if (Array.isArray(value)) return value.map(sort);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, sort(value[key])]));
    return value;
  }
  function assertJson(value, depth = 0) {
    if (depth > 12) fail('INVALID_INPUTS', 'Proje verisi çok fazla iç içe alan içeriyor.');
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
    if (typeof value === 'number') { if (!Number.isFinite(value)) fail('INVALID_INPUTS'); return; }
    if (Array.isArray(value)) { if (value.length > 100) fail('INVALID_INPUTS'); value.forEach(item => assertJson(item, depth + 1)); return; }
    if (!value || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) fail('INVALID_INPUTS');
    for (const [key, item] of Object.entries(value)) {
      if (['__proto__', 'constructor', 'prototype'].includes(key)) fail('INVALID_INPUTS');
      assertJson(item, depth + 1);
    }
  }
  const fresh = () => ({ version: 2, revision: 0, seedVersion: 1, projects: [], notes: [], experiments: [], recipes: [], materials: [], files: [], vault: null });
  function checkedWorkspace(value) {
    if (!value || !Number.isSafeInteger(value.revision) || value.revision < 0) fail('INVALID_RESPONSE');
    for (const type of TYPES) if (!Array.isArray(value[type]) || value[type].length > 500) fail('INVALID_RESPONSE');
    return value;
  }
  function parseRecord(record, type) {
    if (!record || typeof record.id !== 'string' || !record.id) fail('INVALID_RESPONSE');
    let envelope = null;
    try { envelope = JSON.parse(record.body); } catch (_) {}
    const structured = envelope?.version === 3 && envelope?.kind === 'fabrikone-studio' && envelope.inputs && typeof envelope.inputs === 'object';
    return { id: record.id, type, title: record.title || 'Mum projem', status: record.status || 'Taslak', inputs: structured ? clone(envelope.inputs) : null, projectKey: structured ? envelope.projectKey : undefined, legacy: !structured, body: structured ? undefined : String(record.body || ''), fields: clone(record.fields || {}), createdAt: record.createdAt, updatedAt: record.updatedAt, deletedAt: record.deletedAt, archived: !!record.archived };
  }
  function guestStore(themeId) {
    const owner = String(themeId || 'preview') + ':guest';
    if (root.indexedDB) {
      const open = () => new Promise((resolve, reject) => {
        const request = root.indexedDB.open('fabrikone-studyom-draft-v2', 1);
        request.onupgradeneeded = () => { for (const name of ['workspaces', 'blobs']) if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name); };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      return {
        async load() { const db = await open(); return new Promise((resolve, reject) => { const request = db.transaction('workspaces').objectStore('workspaces').get(owner); request.onsuccess = () => { db.close(); resolve(request.result || fresh()); }; request.onerror = () => { db.close(); reject(request.error); }; }); },
        async save(next, revision) { const db = await open(); return new Promise((resolve, reject) => { const tx = db.transaction('workspaces', 'readwrite'), store = tx.objectStore('workspaces'); let conflict = false; const request = store.get(owner); request.onsuccess = () => { if ((request.result?.revision || 0) !== revision) { conflict = true; tx.abort(); } else store.put(next, owner); }; tx.oncomplete = () => { db.close(); resolve(); }; tx.onabort = () => { db.close(); reject(Object.assign(new Error(conflict ? 'CONFLICT' : 'SAVE_FAILED'), { code: conflict ? 'CONFLICT' : 'SAVE_FAILED' })); }; tx.onerror = () => { db.close(); reject(tx.error); }; }); }
      };
    }
    const key = 'fabrikone-studyom-projects-v3:' + owner;
    return {
      async load() { const raw = root.localStorage.getItem(key); return raw ? JSON.parse(raw) : fresh(); },
      async save(next, revision) { const raw = root.localStorage.getItem(key); if ((raw ? JSON.parse(raw).revision : 0) !== revision) fail('CONFLICT'); root.localStorage.setItem(key, JSON.stringify(next)); if (root.localStorage.getItem(key) !== JSON.stringify(next)) fail('SAVE_FAILED'); }
    };
  }
  return function FabrikoneStudioStorage(options = {}) {
    const expected = String(options.customerId || '');
    if (expected && !/^[1-9]\d{0,19}$/.test(expected)) fail('INVALID_CUSTOMER');
    const request = options.request || ((...args) => root.fetch(...args));
    const local = options.guestStore || guestStore(options.themeId);
    const uuid = options.uuid || (() => root.crypto.randomUUID());
    let workspace = null, owner = expected, csrf = '', mode = 'loading', busy = false, uncertain = false, verifiedSave = false;
    const label = () => mode === 'account' ? (verifiedSave ? 'Hesabında kaydedildi' : 'Hesabına bağlı') : mode === 'guest' ? (verifiedSave ? 'Bu cihazda kaydedildi' : 'Bu cihazda çalışma alanı') : 'Kaydedilmedi';
    function view() {
      const records = type => (workspace?.[type] || []).filter(item => !item.deletedAt && !item.archived).map(item => parseRecord(item, type));
      const projects = records('projects'), experiments = records('experiments');
      return { mode, label: label(), revision: workspace?.revision || 0, projects, experiments, all: [...projects, ...experiments] };
    }
    async function call(method, body) {
      const init = { method, credentials: 'same-origin', cache: 'no-store', redirect: 'error', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(18000) };
      if (body) { init.headers['Content-Type'] = 'application/json'; init.headers['X-Studyom-CSRF'] = csrf; init.body = JSON.stringify(body); }
      const response = await request('/apps/studyom/workspace', init);
      let data;
      try { data = await response.json(); } catch (_) { fail('INVALID_RESPONSE'); }
      if (!response.ok) {
        const error = Object.assign(new Error(data?.error || 'CONNECTION_FAILED'), { code: response.status === 409 ? 'CONFLICT' : data?.error || 'CONNECTION_FAILED', status: response.status });
        throw error;
      }
      if (!/^[1-9]\d{0,19}$/.test(String(data.customerId || '')) || typeof data.csrf !== 'string' || !data.csrf) fail('INVALID_RESPONSE');
      if (owner && String(data.customerId) !== owner) { workspace = null; csrf = ''; mode = 'error'; fail('ACCOUNT_CHANGED', 'Hesap değişti. Projeyi kaydetmeden önce tekrar giriş yap.'); }
      owner = String(data.customerId);
      return { workspace: checkedWorkspace(data.workspace), csrf: data.csrf };
    }
    async function load() {
      if (busy) fail('BUSY');
      verifiedSave = false;
      busy = true;
      try {
        let result;
        try { result = await call('GET'); }
        catch (error) {
          if (error.status !== 401 || error.code !== 'LOGIN_REQUIRED') throw error;
          if (owner) { mode = 'error'; csrf = ''; workspace = null; fail('LOGIN_REQUIRED', 'Hesabına giriş yaparak projelerini açabilirsin.'); }
          workspace = checkedWorkspace(await local.load()); mode = 'guest'; csrf = ''; uncertain = false; return view();
        }
        workspace = clone(result.workspace); csrf = result.csrf; mode = 'account'; uncertain = false;
        return view();
      } catch (error) { mode = 'error'; csrf = ''; throw error; }
      finally { busy = false; }
    }
    function valueFor(project, id) {
      if (!project || typeof project !== 'object') fail('INVALID_INPUTS');
      const inputs = project.inputs || Object.fromEntries(Object.entries(project).filter(([key]) => !['id', 'type', 'title', 'status', 'fields', 'projectKey', 'updatedAt', 'createdAt', 'legacy', 'body', 'mode', 'label'].includes(key)));
      assertJson(inputs);
      if (Array.isArray(inputs) || !inputs || typeof inputs !== 'object') fail('INVALID_INPUTS');
      const envelope = { version: 3, kind: 'fabrikone-studio', projectKey: String(project.projectKey || id || uuid()), inputs: clone(inputs) };
      const body = JSON.stringify(envelope);
      if (body.length > 4000) fail('PROJECT_TOO_LARGE', 'Proje alanı doldu. Notunu kısaltıp tekrar kaydet; seçimlerin silinmedi.');
      const title = String(project.title || inputs.name || 'Yeni mum projem').trim();
      if (!title || title.length > 100) fail('INVALID_TITLE', 'Proje adı 1–100 karakter olmalı.');
      const status = project.status || 'Taslak';
      if (!['Taslak', 'Deneme', 'Tamamlandı'].includes(status)) fail('INVALID_STATUS');
      const fields = { source: 'studyom-v3' };
      for (const key of FIELDS) { const input = project.fields?.[key] ?? inputs[key]; if (typeof input === 'string' || typeof input === 'number') fields[key] = String(input).trim().slice(0, key === 'observations' ? 4000 : 300); }
      for (const key of ['waxAmount', 'dyeAmount', 'rate', 'quantity']) if (fields[key] && (!Number.isFinite(Number(fields[key])) || Number(fields[key]) < 0)) fail('INVALID_NUMBER');
      return { value: { title, status, tag: 'Stüdyom', body, fields }, envelope };
    }
    async function write(command, envelope) {
      if (!workspace || !['account', 'guest'].includes(mode)) fail('NOT_READY');
      if (busy) fail('BUSY');
      if (uncertain) fail('SAVE_UNCERTAIN', 'Önceki kayıt sonucu kesinleşmedi. Projeleri yenileyip kaydı kontrol et.');
      busy = true;
      const revision = workspace.revision, beforeIds = new Set(workspace[command.type].map(item => item.id));
      try {
        let next;
        if (mode === 'account') {
          if (!owner || !csrf) fail('LOGIN_REQUIRED');
          let result;
          try { result = await call('POST', { command, baseRevision: revision, requestId: uuid() }); }
          catch (error) {
            if (error.code === 'CONFLICT') { try { const current = await call('GET'); workspace = clone(current.workspace); csrf = current.csrf; } catch (_) {} }
            else if (!error.status) uncertain = true;
            throw error;
          }
          next = result.workspace;
          if (next.revision !== revision + 1) { uncertain = true; fail('SAVE_NOT_VERIFIED'); }
          csrf = result.csrf;
        } else {
          next = clone(workspace); const list = next[command.type], index = list.findIndex(item => item.id === command.id), previous = list[index];
          if (command.id && (!previous || previous.deletedAt)) fail('NOT_FOUND');
          const now = new Date().toISOString();
          if (command.action === 'save') {
            if (!previous && list.length >= 500) fail('STORAGE_FULL');
            const record = { ...command.value, id: previous?.id || uuid(), slot: previous?.slot || list.length + 1, favorite: previous?.favorite || false, example: false, createdAt: previous?.createdAt || now, updatedAt: now, deletedAt: null, archived: previous?.archived || false };
            if (previous) list[index] = record; else list.push(record);
          } else { previous.deletedAt = now; previous.updatedAt = now; previous.favorite = false; }
          next.revision = revision + 1;
          await local.save(next, revision);
          next = checkedWorkspace(await local.load());
        }
        let stored;
        if (command.action === 'save') {
          stored = command.id ? next[command.type].find(item => item.id === command.id) : next[command.type].find(item => !beforeIds.has(item.id) && item.body === command.value.body);
          let parsed;
          try { parsed = JSON.parse(stored?.body || ''); } catch (_) { uncertain = mode === 'account'; fail('SAVE_NOT_VERIFIED', 'Kayıt doğrulanamadı; seçimlerin açık projede korunuyor.'); }
          if (!stored || stored.deletedAt || stored.title !== command.value.title || stored.status !== command.value.status || canonical(parsed) !== canonical(envelope)) { uncertain = mode === 'account'; fail('SAVE_NOT_VERIFIED', 'Sunucu proje seçimlerini eksiksiz saklamadı. Proje kaydedildi olarak işaretlenmedi.'); }
        } else { stored = next[command.type].find(item => item.id === command.id); if (!stored?.deletedAt) fail('SAVE_NOT_VERIFIED'); }
        workspace = clone(next);
        verifiedSave = true;
        return { ...parseRecord(stored, command.type), mode, label: label(), revision: workspace.revision };
      } finally { busy = false; }
    }
    async function save(project, config = {}) {
      verifiedSave = false;
      if (!workspace) await load();
      const type = config.type || project.type || 'projects';
      if (!TYPES.includes(type)) fail('INVALID_TYPE');
      const id = config.id || project.id || undefined;
      if (id && typeof id !== 'string') fail('INVALID_ID');
      const prepared = valueFor({ ...project, status: project.status || (type === 'experiments' ? 'Deneme' : 'Taslak') }, id);
      return write({ action: 'save', type, ...(id ? { id } : {}), value: prepared.value }, prepared.envelope);
    }
    async function softDelete(id, config = {}) {
      if (!workspace) await load();
      const type = config.type || TYPES.find(name => workspace[name].some(item => item.id === id && !item.deletedAt));
      if (!TYPES.includes(type) || typeof id !== 'string' || !workspace[type].some(item => item.id === id && !item.deletedAt)) fail('NOT_FOUND');
      return write({ action: 'delete', type, id });
    }
    return { load, save, softDelete, snapshot: view, get mode() { return mode; } };
  };
});
